import { useEffect, useState, useMemo, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CalendarDays, Plus, Check, X, Clock, Gift, RotateCcw, Sparkles } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';

const STATUS_BADGE = {
  pending: 'bg-amber-100 text-amber-700 border-amber-200',
  approved: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  rejected: 'bg-red-100 text-red-700 border-red-200',
  cancelled: 'bg-slate-100 text-slate-500 border-slate-200',
  marked_working: 'bg-blue-100 text-blue-700 border-blue-200',
};

const LEAVE_TYPE_LABEL = { regular: 'Regular', comp_off: 'Comp Off' };

export default function LeaveCalendar() {
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const role = currentUser.role || 'member';
  const isSA = role === 'super_admin';
  const isAdminOrSA = role === 'super_admin' || role === 'admin';

  // My view (all roles)
  const [myRequests, setMyRequests] = useState([]);
  const [pendingTransfers, setPendingTransfers] = useState([]);
  const [myBalance, setMyBalance] = useState(null);

  // Admin/SA view
  const [allLeaves, setAllLeaves] = useState([]);
  const [pendingLeaves, setPendingLeaves] = useState([]);
  const [allBalances, setAllBalances] = useState([]);

  // Request dialog
  const [showRequestDialog, setShowRequestDialog] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');
  const [leaveType, setLeaveType] = useState('regular');
  const [halfDay, setHalfDay] = useState(false);
  const [loading, setLoading] = useState(false);

  // Comp Off dialog (SA)
  const [showCompOff, setShowCompOff] = useState(false);
  const [coUserId, setCoUserId] = useState('');
  const [coDays, setCoDays] = useState('');
  const [coReason, setCoReason] = useState('');
  const [employees, setEmployees] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const [reqRes, balRes] = await Promise.all([
        api.get('/leave/requests'),
        api.get('/leaves/balance'),
      ]);
      setMyRequests(reqRes.data.my_requests || []);
      setPendingTransfers(reqRes.data.pending_transfers || []);
      setMyBalance(balRes.data);
    } catch (err) {
      console.warn('Leave fetch failed:', err);
    }
    if (isAdminOrSA) {
      try {
        const all = await api.get('/leaves/all');
        setAllLeaves(all.data || []);
      } catch { /* silent */ }
    }
    if (isSA) {
      try {
        const [pend, bals] = await Promise.all([
          api.get('/leaves/pending'),
          api.get('/leaves/balances'),
        ]);
        setPendingLeaves(pend.data || []);
        setAllBalances(bals.data?.employees || []);
      } catch { /* silent */ }
      try {
        const emps = await api.get('/hr/employees');
        setEmployees((emps.data || []).filter(e => e.is_active !== false));
      } catch { /* silent */ }
    }
  }, [isAdminOrSA, isSA]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleRequest = async () => {
    if (!startDate || !endDate) { toast.error('Select both start and end dates'); return; }
    if (halfDay && startDate !== endDate) { toast.error('Half-day leave must be for a single date'); return; }
    setLoading(true);
    try {
      await api.post('/leave/request', {
        start_date: startDate, end_date: endDate,
        reason: reason || 'Leave',
        leave_type: leaveType, half_day: halfDay,
      });
      toast.success('Leave request submitted — awaiting Super Admin approval');
      setShowRequestDialog(false);
      setStartDate(''); setEndDate(''); setReason('');
      setLeaveType('regular'); setHalfDay(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to request leave'); }
    setLoading(false);
  };

  const handleTransferAccept = async (id) => {
    try {
      const res = await api.put(`/leave/transfer/${id}/accept`);
      toast.success(res.data.message);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to accept'); }
  };

  const handleTransferReject = async (id) => {
    try {
      const res = await api.put(`/leave/transfer/${id}/reject`);
      toast.success(res.data.message);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to reject'); }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this leave request?')) return;
    try {
      await api.delete(`/leaves/${id}`);
      toast.success('Leave cancelled');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to cancel'); }
  };

  const handleApprove = async (id) => {
    try {
      const res = await api.post(`/leaves/${id}/approve`);
      const lv = res.data.leave || {};
      toast.success(`Approved: paid=${lv.paid_days ?? 0}, LOP=${lv.lop_days ?? 0}, comp-off=${lv.comp_off_days ?? 0}`);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Approve failed'); }
  };

  const handleReject = async (id) => {
    const r = window.prompt('Reason for rejection (optional):') || '';
    try {
      await api.post(`/leaves/${id}/reject`, { reason: r });
      toast.success('Leave rejected');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Reject failed'); }
  };

  const handleMarkWorking = async (id) => {
    if (!window.confirm('Mark this approved leave as a working day? The paid/comp-off balance will be restored.')) return;
    try {
      await api.post(`/leaves/${id}/mark-working`);
      toast.success('Leave reversed — treated as working day');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to mark working'); }
  };

  const handleGrantCompOff = async () => {
    if (!coUserId || !coDays) { toast.error('Pick employee and days'); return; }
    try {
      await api.post('/leaves/comp-off/grant', {
        user_id: coUserId,
        days: parseFloat(coDays),
        reason: coReason,
      });
      toast.success('Comp Off credited');
      setShowCompOff(false); setCoUserId(''); setCoDays(''); setCoReason('');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleAutoGrant = async () => {
    const month = window.prompt(
      'Auto-credit comp-off for holiday work in month (YYYY-MM). Leave blank for the current calendar year up to today.',
      new Date().toISOString().slice(0, 7),
    );
    if (month === null) return; // Cancel
    try {
      const body = month ? { month: month.trim() } : {};
      const res = await api.post('/leaves/comp-off/auto-grant', body);
      toast.success(`Scanned ${res.data.scanned_holidays} holiday(s): ${res.data.credited} credited, ${res.data.skipped} skipped`);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Auto-grant failed'); }
  };

  const activeRequests = useMemo(() => myRequests.filter(r => r.status !== 'cancelled'), [myRequests]);

  return (
    <div className="p-6 bg-slate-50 min-h-full" data-testid="leave-calendar-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <CalendarDays className="h-6 w-6 text-[#5B6FD6]" /> Leaves
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {isSA ? 'Approve leaves, track balances, grant comp-off' :
             isAdminOrSA ? 'View team leaves (read-only)' :
             'Request leaves and track your balance'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isSA && (
            <Button variant="outline" onClick={handleAutoGrant} data-testid="auto-grant-compoff-btn">
              <Sparkles className="h-4 w-4 mr-1.5" /> Auto-Credit Holiday Work
            </Button>
          )}
          {isSA && (
            <Button variant="outline" onClick={() => setShowCompOff(true)} data-testid="grant-compoff-btn">
              <Gift className="h-4 w-4 mr-1.5" /> Grant Comp Off
            </Button>
          )}
          <Button onClick={() => setShowRequestDialog(true)} className="bg-[#5B6FD6] hover:bg-[#4A5BC4]" data-testid="request-leave-btn">
            <Plus className="h-4 w-4 mr-1.5" /> Request Leave
          </Button>
        </div>
      </div>

      {/* My balance card */}
      {myBalance && (
        <Card className="mb-5 border-slate-200" data-testid="my-balance-card">
          <CardContent className="py-4">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <BalanceStat label="Paid accrued" value={myBalance.paid_accrued} tone="slate" testid="bal-paid-accrued" />
              <BalanceStat label="Paid used" value={myBalance.paid_used} tone="amber" testid="bal-paid-used" />
              <BalanceStat label="Paid available" value={myBalance.paid_available} tone="emerald" testid="bal-paid-available" />
              <BalanceStat label="Comp-Off earned" value={myBalance.comp_off_earned} tone="slate" testid="bal-co-earned" />
              <BalanceStat label="Comp-Off available" value={myBalance.comp_off_available} tone="emerald" testid="bal-co-available" />
            </div>
            <p className="text-[11px] text-slate-400 mt-3">
              5 paid leaves accrue every quarter and accumulate through the calendar year. All unused paid leaves lapse on Dec 31. Requests beyond your paid balance are auto-classified as LOP by the Super Admin at approval.
            </p>
          </CardContent>
        </Card>
      )}

      {/* SA: pending approvals */}
      {isSA && (
        <Card className="mb-5 border-amber-200 bg-amber-50/40" data-testid="sa-pending-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-amber-800 flex items-center gap-2">
              <Clock className="h-4 w-4" /> Pending Approvals ({pendingLeaves.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {pendingLeaves.length === 0 ? (
              <p className="px-5 py-6 text-sm text-slate-400 text-center">No leaves pending approval</p>
            ) : pendingLeaves.map(l => (
              <div key={l.id} className="flex items-center justify-between px-5 py-3 border-t border-amber-100 first:border-t-0" data-testid={`sa-pending-${l.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm text-slate-900">{l.user_name}</span>
                    <span className="text-slate-400 mx-1">·</span>
                    <span className="text-xs text-slate-500">
                      {l.start_date}{l.start_date !== l.end_date ? ` → ${l.end_date}` : ''} · {l.total_days}d
                    </span>
                    <Badge variant="outline" className="text-[10px] bg-slate-50 text-slate-600 border-slate-200">
                      {LEAVE_TYPE_LABEL[l.leave_type] || l.leave_type}
                    </Badge>
                    {l.half_day && <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-600 border-blue-200">Half day</Badge>}
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">{l.reason || '—'}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-8 px-3 text-xs" onClick={() => handleApprove(l.id)} data-testid={`approve-leave-${l.id}`}>
                    <Check className="h-3 w-3 mr-1" /> Approve
                  </Button>
                  <Button size="sm" variant="outline" className="h-8 px-3 text-xs text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleReject(l.id)} data-testid={`reject-leave-${l.id}`}>
                    <X className="h-3 w-3 mr-1" /> Reject
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Backup transfer requests needing my acceptance */}
      {pendingTransfers.length > 0 && (
        <Card className="mb-5 border-amber-200 bg-amber-50/40">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-amber-800">Task Coverage Requests</CardTitle>
            <p className="text-xs text-slate-500 mt-0.5">Your teammates are requesting leave and need you to cover their tasks.</p>
          </CardHeader>
          <CardContent className="p-0">
            {pendingTransfers.map(req => (
              <div key={req.id} className="flex items-center justify-between px-5 py-3 border-t border-amber-100 first:border-t-0" data-testid={`transfer-req-${req.id}`}>
                <div>
                  <span className="font-medium text-sm text-slate-900">{req.user_name}</span>
                  <span className="text-slate-400 mx-2">·</span>
                  <span className="text-xs text-slate-500">{req.start_date} → {req.end_date}</span>
                  <p className="text-xs text-slate-400 mt-0.5">{req.task_count} task(s) · {req.reason}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600 h-8 px-3 text-xs" onClick={() => handleTransferAccept(req.id)} data-testid={`accept-transfer-${req.id}`}>
                    <Check className="h-3 w-3 mr-1" /> Accept
                  </Button>
                  <Button size="sm" variant="outline" className="h-8 px-3 text-xs text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleTransferReject(req.id)} data-testid={`reject-transfer-${req.id}`}>
                    <X className="h-3 w-3 mr-1" /> Reject
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* My leave requests */}
      <Card className="border-slate-200 mb-5" data-testid="my-leaves-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-slate-700">My Leave Requests</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {activeRequests.length === 0 ? (
            <p className="px-5 py-6 text-sm text-slate-400 text-center">No leave requests yet</p>
          ) : activeRequests.map(req => (
            <div key={req.id} className="flex items-center justify-between px-5 py-3 border-t border-slate-100 first:border-t-0 hover:bg-slate-50/50" data-testid={`my-leave-req-${req.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium text-slate-900">
                    {req.start_date}{req.start_date !== req.end_date ? ` → ${req.end_date}` : ''}
                  </span>
                  <Badge variant="outline" className="text-[10px] bg-slate-50 text-slate-600 border-slate-200">
                    {LEAVE_TYPE_LABEL[req.leave_type] || 'Regular'} · {req.total_days || 0}d
                  </Badge>
                  <Badge variant="outline" className={`text-[10px] ${STATUS_BADGE[req.status] || STATUS_BADGE.pending}`}>{req.status}</Badge>
                  {req.status === 'approved' && (
                    <span className="text-[11px] text-slate-500">
                      paid {req.paid_days || 0} · LOP {req.lop_days || 0}{(req.comp_off_days || 0) > 0 ? ` · comp-off ${req.comp_off_days}` : ''}
                    </span>
                  )}
                  {req.backup_name && req.status === 'pending' && req.transfer_status === 'pending' && (
                    <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-600 border-amber-200">
                      <Clock className="h-2.5 w-2.5 mr-0.5" /> Awaiting {req.backup_name}
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-slate-400 mt-0.5">{req.reason || '—'}</p>
                {req.sa_decision_reason && (
                  <p className="text-[11px] text-slate-500 mt-0.5">SA note: {req.sa_decision_reason}</p>
                )}
              </div>
              {req.status === 'pending' && req.user_id === currentUser.id && (
                <Button size="sm" variant="ghost" className="text-xs text-slate-400 hover:text-red-600" onClick={() => handleCancel(req.id)} data-testid={`cancel-leave-${req.id}`}>
                  Cancel
                </Button>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Admin/SA: all leaves */}
      {isAdminOrSA && (
        <Card className="border-slate-200 mb-5" data-testid="team-leaves-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-700">All Team Leaves</CardTitle>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-2">Employee</th>
                  <th className="text-left px-4 py-2">Dates</th>
                  <th className="text-left px-4 py-2">Type</th>
                  <th className="text-left px-4 py-2">Days</th>
                  <th className="text-left px-4 py-2">Status</th>
                  <th className="text-left px-4 py-2">Split</th>
                  <th className="text-left px-4 py-2">Reason</th>
                  {isSA && <th className="text-left px-4 py-2 w-32">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {allLeaves.length === 0 ? (
                  <tr><td colSpan={isSA ? 8 : 7} className="px-4 py-6 text-center text-slate-400">No leaves recorded</td></tr>
                ) : allLeaves.map(l => (
                  <tr key={l.id} className="hover:bg-slate-50" data-testid={`all-leave-${l.id}`}>
                    <td className="px-4 py-2 font-medium text-slate-900">{l.user_name}</td>
                    <td className="px-4 py-2 text-slate-600">
                      {l.start_date}{l.start_date !== l.end_date ? ` → ${l.end_date}` : ''}
                    </td>
                    <td className="px-4 py-2 text-slate-600">{LEAVE_TYPE_LABEL[l.leave_type] || 'Regular'}{l.half_day ? ' (½)' : ''}</td>
                    <td className="px-4 py-2 text-slate-600">{l.total_days || 0}</td>
                    <td className="px-4 py-2">
                      <Badge variant="outline" className={`text-[10px] ${STATUS_BADGE[l.status] || STATUS_BADGE.pending}`}>{l.status}</Badge>
                    </td>
                    <td className="px-4 py-2 text-slate-500 text-[11px]">
                      {l.status === 'approved' ? `p${l.paid_days || 0} · L${l.lop_days || 0} · c${l.comp_off_days || 0}` : '—'}
                    </td>
                    <td className="px-4 py-2 text-slate-500 max-w-xs truncate" title={l.reason || ''}>{l.reason || '—'}</td>
                    {isSA && (
                      <td className="px-4 py-2">
                        {l.status === 'pending' && (
                          <div className="flex gap-1">
                            <Button size="sm" className="h-7 px-2 bg-emerald-600 hover:bg-emerald-700 text-[11px]" onClick={() => handleApprove(l.id)} data-testid={`row-approve-${l.id}`}>
                              <Check className="h-3 w-3" />
                            </Button>
                            <Button size="sm" variant="outline" className="h-7 px-2 text-[11px] text-red-600 border-red-200" onClick={() => handleReject(l.id)} data-testid={`row-reject-${l.id}`}>
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        )}
                        {l.status === 'approved' && (
                          <Button size="sm" variant="outline" className="h-7 px-2 text-[11px] text-blue-600 border-blue-200 hover:bg-blue-50" onClick={() => handleMarkWorking(l.id)} data-testid={`row-mark-working-${l.id}`}>
                            <RotateCcw className="h-3 w-3 mr-1" /> Mark working
                          </Button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* SA: all balances */}
      {isSA && (
        <Card className="border-slate-200" data-testid="all-balances-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-700">Team Balances ({myBalance?.year || new Date().getFullYear()})</CardTitle>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-2">Employee</th>
                  <th className="text-left px-4 py-2">Paid accrued</th>
                  <th className="text-left px-4 py-2">Paid used</th>
                  <th className="text-left px-4 py-2">Paid available</th>
                  <th className="text-left px-4 py-2">Comp-Off earned</th>
                  <th className="text-left px-4 py-2">Comp-Off used</th>
                  <th className="text-left px-4 py-2">Comp-Off available</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {allBalances.map(b => (
                  <tr key={b.user_id} className="hover:bg-slate-50" data-testid={`balance-row-${b.user_id}`}>
                    <td className="px-4 py-2 font-medium text-slate-900">{b.full_name}</td>
                    <td className="px-4 py-2 text-slate-600">{b.paid_accrued}</td>
                    <td className="px-4 py-2 text-amber-600">{b.paid_used}</td>
                    <td className="px-4 py-2 text-emerald-600 font-medium">{b.paid_available}</td>
                    <td className="px-4 py-2 text-slate-600">{b.comp_off_earned}</td>
                    <td className="px-4 py-2 text-amber-600">{b.comp_off_used}</td>
                    <td className="px-4 py-2 text-emerald-600 font-medium">{b.comp_off_available}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Request Leave Dialog */}
      <Dialog open={showRequestDialog} onOpenChange={setShowRequestDialog}>
        <DialogContent className="sm:max-w-md" data-testid="request-leave-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><CalendarDays className="h-5 w-5 text-[#5B6FD6]" /> Request Leave</DialogTitle>
            <DialogDescription>
              Backdated and future dates are both allowed. Requires Super Admin approval before it counts.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Leave Type</Label>
                <Select value={leaveType} onValueChange={setLeaveType}>
                  <SelectTrigger data-testid="leave-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="regular">Regular (paid balance)</SelectItem>
                    <SelectItem value="comp_off">Comp Off</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-sm text-slate-700 pb-1 select-none">
                  <input type="checkbox" checked={halfDay} onChange={(e) => setHalfDay(e.target.checked)} data-testid="leave-half-day" />
                  Half day
                </label>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Start Date</Label>
                <Input type="date" value={startDate} onChange={e => { setStartDate(e.target.value); if (halfDay) setEndDate(e.target.value); }} data-testid="leave-start-date" />
              </div>
              <div>
                <Label>End Date</Label>
                <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} min={startDate} disabled={halfDay} data-testid="leave-end-date" />
              </div>
            </div>
            <div>
              <Label>Reason (optional)</Label>
              <Input placeholder="e.g. Family vacation, Medical leave" value={reason} onChange={e => setReason(e.target.value)} data-testid="leave-reason" />
            </div>
            {leaveType === 'regular' && myBalance && (
              <p className="text-xs text-slate-500 bg-slate-50 p-2 rounded">
                Paid balance: <b>{myBalance.paid_available}</b> day(s). Anything beyond will be classified as LOP.
              </p>
            )}
            {leaveType === 'comp_off' && myBalance && (
              <p className="text-xs text-slate-500 bg-slate-50 p-2 rounded">
                Comp-Off balance: <b>{myBalance.comp_off_available}</b> day(s). Overflow falls back to paid, then LOP.
              </p>
            )}
            <Button className="w-full bg-[#5B6FD6] hover:bg-[#4A5BC4]" onClick={handleRequest} disabled={!startDate || !endDate || loading} data-testid="submit-leave-btn">
              {loading ? 'Submitting...' : 'Submit Leave Request'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* SA Grant Comp Off Dialog */}
      {isSA && (
        <Dialog open={showCompOff} onOpenChange={setShowCompOff}>
          <DialogContent className="sm:max-w-md" data-testid="grant-compoff-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Gift className="h-5 w-5 text-emerald-600" /> Grant Comp Off</DialogTitle>
              <DialogDescription>Credit compensatory off days into an employee&apos;s pool. Employees pick &apos;Comp Off&apos; at request time to burn it.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Employee</Label>
                <Select value={coUserId} onValueChange={setCoUserId}>
                  <SelectTrigger data-testid="co-employee-select"><SelectValue placeholder="Choose employee" /></SelectTrigger>
                  <SelectContent>
                    {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Days to grant (step 0.5)</Label>
                <Input type="number" step="0.5" min="0.5" value={coDays} onChange={e => setCoDays(e.target.value)} data-testid="co-days" />
              </div>
              <div>
                <Label>Reason (optional)</Label>
                <Input placeholder="Worked on Republic Day, Diwali overtime, etc." value={coReason} onChange={e => setCoReason(e.target.value)} data-testid="co-reason" />
              </div>
              <Button className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={handleGrantCompOff} data-testid="submit-compoff-btn">Grant</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

const BalanceStat = ({ label, value, tone = 'slate', testid }) => {
  const bg = {
    slate: 'bg-slate-50 text-slate-700',
    amber: 'bg-amber-50 text-amber-700',
    emerald: 'bg-emerald-50 text-emerald-700',
  }[tone];
  return (
    <div className={`rounded-md px-3 py-2 ${bg}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-xl font-semibold mt-0.5">{value}</div>
    </div>
  );
};
